/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useCallback,
  useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Button, Col, Modal, Row, Form as BForm,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import validateForm from 'utils/Validator';
import useSubmit from 'hooks/useSubmit';
import Form from 'components/Form/Layout';
import { yupCreateEvent } from 'utils/yupFields';
import { LoadingContext } from 'context/Loading';
import AdminInvitationsServices from 'services/api/AdminInvitations';
import TextField from 'components/Form/FormTextField';
import { AiOutlinePlus } from 'react-icons/ai';
import Select from 'components/Form/FormSelect';
import { FiMapPin } from 'react-icons/fi';
import { IoCallOutline } from 'react-icons/io5';
import Timepicker from 'components/Form/FormTimepicker';
import Datepicker from 'components/Form/FormDatepicker';
import InputFile from 'components/Form/FormInputFile';
import 'react-checkbox-tree/src/scss/react-checkbox-tree.scss';
import { BsTrashFill } from 'react-icons/bs';
import { FormApi } from 'final-form';
import {
  AdminEventForm, EventCategoryType, EventFile, EventProps,
} from 'types/Event';
import handlePromise from 'utils/Promise';
import EventsApi from 'services/api/Event';
import { allDayEnd, mergeDateAndTime, mergeDateAndTimeUTC } from 'utils/date';
import moment from 'moment';
import ProjectService from 'services/api/Projects';
import SuccessMessage from '../SuccessMessage';

type CreateEventModalProps = {
  data?: EventProps,
  onClose: () => void,
  reloadData: () => void,
};

function CreateEventModal( { data, onClose, reloadData }: CreateEventModalProps ) {
  const { t } = useTranslation();
  const { toggleLoading } = useContext( LoadingContext );
  const [formSubmitted, setFormSubmitted] = useState( false );
  const [activeProjects, setActiveProjects] = useState<any[]>();
  const validate = validateForm( yupCreateEvent );

  const getProjects = useCallback( async () => {
    const [error, response, projects] = await handlePromise(
      ProjectService.getProjectsList(),
    );
    if ( response.ok ) {
      setActiveProjects( projects.data );
    } else {
      setActiveProjects( [] );
    }
  }, [] );

  useEffect( () => {
    getProjects();
  }, [] );

  const submitForm = useCallback( async ( formValues: AdminEventForm ) => {
    toggleLoading( true );

    let category: EventCategoryType;
    if ( formValues.categoryOnline && formValues.categoryInPerson ) {
      category = EventCategoryType.hibrid;
    } else if ( formValues.categoryOnline ) {
      category = EventCategoryType.online;
    } else {
      category = EventCategoryType.inPerson;
    }
    const dataToSend = {
      titulo: formValues.titulo,
      descripcion: formValues.descripcion,
      fechaInicio: formValues.allDate
        ? mergeDateAndTime( formValues.fechaInicio, '00:00:00' )
        : mergeDateAndTimeUTC( formValues.fechaInicio, `${formValues.horaInicio}:00` ),
      fechaFin: formValues.allDate
        ? allDayEnd( formValues.fechaInicio )
        : mergeDateAndTimeUTC( formValues.fechaFin, `${formValues.horaFin}:00` ),
      categoria: category,
      direccion: formValues.direccion,
      activo: true,
      allDate: formValues.allDate,
      emailsInvitaciones: formValues.emails,
      enviarInvitacion: formValues.noSendInvitation !== true,
    };

    const [errors, response, responseData] = await handlePromise(
      data?.eventoId
        ? EventsApi.editAdminEvent( data?.eventoId, dataToSend )
        : EventsApi.createAdminEvent( formValues.promocionId, dataToSend ),
    );

    if ( response.ok ) {
      if ( formValues.files ) {
        const [filesErrors, filesResponse] = await handlePromise(
          EventsApi.uploadEventFiles( responseData.eventoId, formValues.files as File[] ),
        );
        if ( !filesResponse.ok ) {
          toggleLoading( false );
          return;
        }
      }

      const eventFileIds = new Set( formValues.eventFiles?.map( ( item ) => item.id ) );
      const deletedFiles = data?.ficheros?.filter( ( item ) => !eventFileIds.has( item.id ) );
      if ( deletedFiles?.length ) {
        const [, deleteFilesResponse] = await handlePromise(
          EventsApi.deleteAdminEventFile( responseData.eventoId, deletedFiles.map(
            ( item ) => item.id,
          ) ),
        );
        if ( !deleteFilesResponse.ok ) {
          toggleLoading( false );
          return;
        }
      }

      setFormSubmitted( true );
      reloadData();
    }
    toggleLoading( false );
    // ToDo show error
  }, [] );

  const initialValues = useMemo( () => ( data?.eventoId ? {
    categoryOnline: data?.categoria === EventCategoryType.hibrid
    || data?.categoria === EventCategoryType.online,
    categoryInPerson: data?.categoria === EventCategoryType.hibrid
    || data?.categoria === EventCategoryType.inPerson,
    titulo: data?.titulo,
    descripcion: data?.descripcion,
    direccion: data?.direccion,
    allDate: data?.allDate,
    fechaInicio: moment.utc( data?.fechaInicio ).local().format( 'DD/MM/YYYY' ),
    horaInicio: data?.allDate ? undefined : moment.utc( data?.fechaInicio ).local().format( 'HH:mm' ),
    fechaFin: data?.allDate ? undefined : moment.utc( data?.fechaFin ).local().format( 'DD/MM/YYYY' ),
    horaFin: data?.allDate ? undefined : moment.utc( data?.fechaFin ).local().format( 'HH:mm' ),
    emails: data?.emailsInvitaciones,
    eventFiles: data?.ficheros,
    promocionId: data?.promocionId,
    noSendInvitation: data?.enviarInvitacion,
  } : {
    emails: [],
    files: [],
    eventFiles: [],
    categoryOnline: true,
    allDate: false,
    noSendInvitation: false,
  } ), [data] );

  const deleteNewAttachment = useCallback( ( form: FormApi, index: number ) => {
    const formFiles = form.getFieldState( 'files' )?.value;
    const files = formFiles ? [...formFiles] : [];
    files?.splice( index, 1 );
    form.change( 'files', files );
  }, [] );

  const deleteOldAttachment = useCallback( ( form: FormApi, index: number ) => {
    const formFiles = form.getState()?.values?.eventFiles;
    const files = formFiles ? [...formFiles] : [];
    files?.splice( index, 1 );
    form.change( 'eventFiles', files );
  }, [] );

  return (
    <Modal
      show
      className="modal-carousel"
      centered
      size={formSubmitted ? 'lg' : 'xl'}
      onHide={onClose}
    >
      <Modal.Header className="pb-0" closeButton />
      <Modal.Body className="pt-0">
        <Row>
          <Col md={11} className="m-auto">
            <h3 className="text-center mb-4">{data?.eventoId ? t( 'admin.common.editEvent' ) : t( 'admin.common.createEvent' )}</h3>
            {formSubmitted
              ? (
                <SuccessMessage
                  description={data?.eventoId
                    ? t( 'admin.common.editEventSuccess' ) : t( 'admin.common.createEventSuccess' )}
                  onClose={onClose}
                />
              )
              : (
                <Form
                  validate={validate}
                  onSubmit={( values ) => submitForm( values as AdminEventForm )}
                  initialValues={initialValues}
                  body={( form ) => {
                    const values = form.getState()?.values;

                    const handleAddEmail = async () => {
                      const currentEmail = values.email;

                      const emailSchema = yup
                        .string()
                        .email( t( 'admin.event.errors.invalidEmail' ) )
                        .required( t( 'admin.event.errors.required' ) );

                      try {
                        await emailSchema.validate( currentEmail );

                        if ( ( values.emails || [] ).includes( currentEmail ) ) {
                          form.change( 'emailError', t( 'admin.event.errors.duplicatedEmail' ) );
                          return;
                        }

                        form.change( 'emails', [...( values.emails || [] ), currentEmail] );
                        form.change( 'email', undefined );
                        form.change( 'emailError', undefined );
                        form.resetFieldState( 'email' );
                      } catch ( err: any ) {
                        form.change( 'emailError', err.message );
                      }
                    };

                    return (
                      <div className="mt-3">
                        <Row>
                          <Col md={6}>
                            <h4 className="text-secondary mb-3">¿Cómo te gustaría reunirte?</h4>
                            <Row className="gx-2 mb-4">
                              <Col md="auto">
                                <Button
                                  size="sm"
                                  variant={values.categoryOnline ? 'primary' : 'outline-primary'}
                                  className="btn--rounded d-flex align-items-center"
                                  onClick={() => {
                                    form.change( 'categoryOnline', !values.categoryOnline );
                                  }}
                                >
                                  <IoCallOutline className="me-2" />
                                  {t( 'admin.event.teams' )}
                                </Button>
                              </Col>
                              <Col md="auto">
                                <Button
                                  size="sm"
                                  variant={values.categoryInPerson ? 'primary' : 'outline-primary'}
                                  className="btn--rounded d-flex align-items-center"
                                  onClick={() => {
                                    form.change( 'categoryInPerson', !values.categoryInPerson );
                                  }}
                                >
                                  <FiMapPin className="me-2" />
                                  {t( 'admin.event.inPerson' )}
                                </Button>
                              </Col>
                            </Row>

                            {values.categoryInPerson && (
                            <div className="pt-2">
                              <TextField
                                field="direccion"
                                label="admin.event.direccion"
                                appendIcon="icon-map-pin"
                              />
                            </div>
                            )}

                            <TextField
                              field="titulo"
                              label="admin.event.title"
                              appendIcon="icon-folder"
                            />
                            <TextField
                              field="descripcion"
                              label="admin.event.descripcion"
                              appendIcon="icon-folder"
                            />
                            <Select
                              options={activeProjects || []}
                              field="promocionId"
                              idKey="promocionId"
                              labelKey="codigoPromocion"
                              label="admin.event.promotion"
                              isLoading={!activeProjects}
                              isDisabled={!!data?.eventoId}
                            />

                            <Row className="gx-1 align-items-center mb-4">
                              <Col xs="auto">
                                <BForm.Check
                                  type="switch"
                                  className="switch-generic"
                                  checked={!!values.allDate}
                                  onChange={( event ) => {
                                    form.change( 'allDate', event.target.checked );
                                  }}
                                />
                              </Col>
                              <Col>
                                <BForm.Label className="m-0">{t( 'admin.event.allDay' )}</BForm.Label>
                              </Col>

                              <Col xs="auto">
                                <BForm.Check
                                  type="switch"
                                  className="switch-generic"
                                  checked={!!values.noSendInvitation}
                                  disabled={!!data?.eventoId}
                                  onChange={( event ) => {
                                    form.change( 'noSendInvitation', event.target.checked );
                                  }}
                                />
                              </Col>
                              <Col>
                                <BForm.Label className="m-0">{t( 'admin.event.noSendInvitation' )}</BForm.Label>
                              </Col>
                            </Row>

                            <Row className="gx-2">
                              <Col md={8}>
                                <Datepicker
                                  field="fechaInicio"
                                  label="admin.event.fechaInicio"
                                  appendIcon="icon-calendar"
                                />
                              </Col>
                              <Col>
                                <Timepicker
                                  field="horaInicio"
                                  label="admin.event.horaInicio"
                                  appendIcon="icon-calendar"
                                  disabled={values.allDate}
                                />
                              </Col>
                            </Row>

                            <Row className="gx-2">
                              <Col md={8}>
                                <Datepicker
                                  field="fechaFin"
                                  label="admin.event.fechaFin"
                                  appendIcon="icon-calendar"
                                  disabled={values.allDate}
                                />
                              </Col>
                              <Col>
                                <Timepicker
                                  field="horaFin"
                                  label="admin.event.horaFin"
                                  appendIcon="icon-calendar"
                                  disabled={values.allDate}
                                />
                              </Col>
                            </Row>
                          </Col>

                          <Col>
                            <div className="mb-3">
                              <h4 className="text-secondary mb-3">Añadir archivos</h4>
                              <InputFile
                                field="files"
                                placeholder="admin.event.files"
                                dropzoneOptions={{
                                  multiple: true,
                                }}
                                classNames={{
                                  formGroup: 'mb-1',
                                }}
                              />
                              {values?.files?.map( ( file: File, index: number ) => (
                                <Row key={( file as File ).size} className="align-items-center">
                                  <Col md="auto">
                                    {( file as File ).name}
                                  </Col>
                                  <Col md="auto">
                                    <Button
                                      variant="link"
                                      className="p-0"
                                      onClick={() => deleteNewAttachment( form, index )}
                                    >
                                      <BsTrashFill />
                                    </Button>
                                  </Col>
                                </Row>
                              ) )}
                              {values?.eventFiles?.map( ( file: EventFile, index: number ) => (
                                <Row key={file.id} className="align-items-center">
                                  <Col md="auto">
                                    {file.nombre}
                                  </Col>
                                  <Col md="auto">
                                    <Button
                                      variant="link"
                                      className="p-0"
                                      onClick={() => deleteOldAttachment( form, index )}
                                    >
                                      <BsTrashFill />
                                    </Button>
                                  </Col>
                                </Row>
                              ) )}
                            </div>

                            <Row className="gx-1">
                              <h4 className="text-secondary mb-3">Añadir participantes</h4>
                              <Col>
                                <TextField
                                  field="email"
                                  placeholder="admin.event.emailsToAdd"
                                  // showErrors={false}
                                  classNames={{ formGroup: 'mb-0' }}
                                  onKeyDown={( e: any ) => {
                                    if ( e.key === 'Enter' ) {
                                      e.preventDefault();
                                      handleAddEmail();
                                    }
                                  }}
                                />
                                {values.emailError && (
                                <div className="invalid-feedback d-block">{values.emailError}</div>
                                )}
                              </Col>
                              <Col xs="auto">
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={handleAddEmail}
                                >
                                  <AiOutlinePlus />
                                </Button>
                              </Col>
                            </Row>

                            <div className="email-list mt-4 mb-3 p-2 border rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                              {( values.emails || [] ).map( ( email: string, index: number ) => (
                                <Row key={email} className="align-items-center mb-1">
                                  <Col>{email}</Col>
                                  <Col xs="auto">
                                    <Button
                                      size="sm"
                                      variant="link"
                                      onClick={() => {
                                        const newEmails = [
                                          ...values.emails.slice( 0, index ),
                                          ...values.emails.slice( index + 1 ),
                                        ];
                                        form.change( 'emails', newEmails );
                                      }}
                                    >
                                      <i className="icon-cancel" />
                                    </Button>
                                  </Col>
                                </Row>
                              ) )}
                              {( values.emails || [] ).length === 0 && (
                              <div className="text-muted">{t( 'admin.event.noEmailsAdded' )}</div>
                              )}
                            </div>
                          </Col>

                        </Row>
                        <Row>
                          <Col md={8} className="mt-4 mx-auto">
                            <Button
                              variant="primary"
                              type="submit"
                              className="btn--full-width"
                            >
                              {t( 'admin.common.create' )}
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    );
                  }}
                />
              )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default CreateEventModal;
